import React, { useEffect, useRef, useState } from 'react';

import {
  Bell,
  ChevronDown,
  KeyRound,
  LogOut,
  Menu,
  Moon,
  Sun,
  User,
} from 'lucide-react';
import { useAppSelector } from '../hooks';
import { useNavigate } from 'react-router-dom';
import { useUISettings } from '../context/uiSettingsContext';
import type { HeaderProps } from '../model';
import ChangePasswordModal from '../modules/home/changePassword/ChangePasswordModal';

import {
  useGetAdminNotificationCountQuery,
  useGetAdminNotificationsQuery,
  useMarkAllNotificationsAsReadMutation,
  useMarkNotificationAsReadMutation,
} from '../modules/notifications/notificationsApi';
import type { RootState } from '../store';
import { formatTimestampToDateTime } from '../utils/common';
import type { NotificationRecord } from '../modules/notifications/model';
import BrandLink from './BrandLink';
import UserAvatar from './UserAvatar';
import { getNotificationRoute } from '../modules/notifications/notificationHelpers';

const Header: React.FC<HeaderProps> = ({ onRequestLogout, onMobileMenuOpen }) => {
  const navigate = useNavigate();
  const details = useAppSelector(
    (store: RootState) => store?.profile?.details
  ) as any;
  const { isSidebarCollapsed, themeMode, toggleThemeMode } = useUISettings();

  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showNotificationsDropdown, setShowNotificationsDropdown] =
    useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);

  const userRef = useRef<HTMLDivElement>(null);
  const notificationsRef = useRef<HTMLDivElement>(null);

  const userName = details?.name ?? '';
  const userEmail = details?.email ?? '';
  const profileImageUrl = details?.profileImageUrl;

  const { data: notificationCountData } =
    useGetAdminNotificationCountQuery(undefined);
  const { data: notificationsData, isFetching: isNotificationsLoading } =
    useGetAdminNotificationsQuery(
      { page: 1, limit: 10 },
      { skip: !showNotificationsDropdown, refetchOnMountOrArgChange: true }
    );
  const [markAsRead] = useMarkNotificationAsReadMutation();
  const [markAllAsRead, { isLoading: isMarkingAllRead }] =
    useMarkAllNotificationsAsReadMutation();

  const unreadCount = notificationCountData?.data?.count ?? 0;
  const notifications = notificationsData?.data ?? [];

  const handleNotificationClick = async (notification: NotificationRecord) => {
    if (notification.isRead === 0) {
      try {
        await markAsRead(notification.userNotificationId).unwrap();
      } catch {
        // Allow navigation even if mark-as-read fails.
      }
    }

    setShowNotificationsDropdown(false);

    const targetRoute = getNotificationRoute(notification);
    if (targetRoute) {
      navigate(targetRoute);
    }
  };

  const handleEditProfile = () => {
    setShowUserDropdown(false);
    navigate('/my-profile');
  };

  const handleChangePassword = () => {
    setShowUserDropdown(false);
    setShowChangePasswordModal(true);
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        notificationsRef.current &&
        !notificationsRef.current.contains(event.target as Node)
      ) {
        setShowNotificationsDropdown(false);
      }
      if (userRef.current && !userRef.current.contains(event.target as Node)) {
        setShowUserDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <header
      className="sticky top-0 bg-white dark:bg-neutral-950 border-b border-neutral-200 dark:border-neutral-800"
      style={{ zIndex: 'var(--z-header)' }}
    >
      <div className="h-(--header-height) px-4 md:px-6 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 flex-1">
          {/* Mobile hamburger — only visible below md breakpoint */}
          <button
            type="button"
            className="md:hidden flex items-center justify-center w-9 h-9 rounded-lg text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-900 transition-colors"
            onClick={onMobileMenuOpen}
            aria-label="Open menu"
          >
            <Menu className="w-5 h-5" />
          </button>
          {isSidebarCollapsed && (
            <BrandLink
              showText={true}
              className="shrink-0 hidden md:flex"
              logoClassName="h-8 w-auto object-contain"
            />
          )}
        </div>

        <div className="flex items-center gap-1.5">
          <div className="relative" ref={notificationsRef}>
            <button
              type="button"
              onClick={() => {
                setShowUserDropdown(false);
                setShowNotificationsDropdown((prev) => !prev);
              }}
              className="relative w-9 h-9 flex items-center justify-center text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-900 rounded-lg transition-colors"
              title="Notifications"
            >
              <Bell className="w-4 h-4 text-brand-600 dark:text-brand-400" />
              {unreadCount > 0 && (
                <span className="absolute top-0 right-0 min-w-[18px] h-[18px] px-1 bg-error-500 text-white text-[11px] font-semibold rounded-full flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
            </button>

            {showNotificationsDropdown && (
              <div className="absolute right-0 top-full mt-2 w-80 bg-white dark:bg-neutral-950 border border-neutral-200 dark:border-neutral-800 rounded-lg shadow-xl overflow-hidden">
                <div className="p-3 border-b border-neutral-200 dark:border-neutral-800 flex items-center justify-between">
                  <div className="text-sm font-semibold text-neutral-900 dark:text-white">
                    Notifications
                  </div>
                  <div className="flex items-center gap-3">
                    {unreadCount > 0 && (
                      <span className="text-sm text-brand-600 dark:text-brand-400 font-medium">
                        {unreadCount} new
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() => markAllAsRead()}
                      disabled={unreadCount === 0 || isMarkingAllRead}
                      className="text-xs font-medium text-brand-600 disabled:text-neutral-400 dark:text-brand-400 dark:disabled:text-neutral-500"
                    >
                      Mark all as read
                    </button>
                  </div>
                </div>
                <div className="max-h-96 overflow-y-auto">
                  {isNotificationsLoading ? (
                    <div className="p-4 text-sm text-neutral-500 dark:text-neutral-400">
                      Loading notifications...
                    </div>
                  ) : notifications.length === 0 ? (
                    <div className="p-4 text-sm text-neutral-500 dark:text-neutral-400">
                      No notifications yet.
                    </div>
                  ) : (
                    notifications.map((notification) => {
                      const isUnread = notification.isRead === 0;
                      return (
                        <div
                          key={notification.userNotificationId}
                          onClick={() => handleNotificationClick(notification)}
                          className={`p-3 border-b border-neutral-100 dark:border-neutral-800 hover:bg-neutral-50 dark:hover:bg-neutral-900 transition-colors cursor-pointer ${
                            isUnread ? 'bg-brand-50/30 dark:bg-brand-950/30' : ''
                          }`}
                        >
                          <div className="flex items-start gap-2">
                            {isUnread && (
                              <div className="w-2 h-2 bg-brand-600 dark:bg-brand-400 rounded-full mt-1.5 shrink-0" />
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-neutral-900 dark:text-white mb-0.5">
                                {notification.title || notification.notificationCategory}
                              </div>
                              <div className="text-xs text-neutral-600 dark:text-neutral-400 mb-1">
                                {notification.message}
                              </div>
                              <div className="text-xs text-neutral-500 dark:text-neutral-500">
                                {formatTimestampToDateTime(
                                  notification.addedDate
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
                <div className="p-2 border-t border-neutral-200 dark:border-neutral-800">
                  <button
                    type="button"
                    onClick={() => {
                      setShowNotificationsDropdown(false);
                      navigate('/notifications');
                    }}
                    className="w-full py-2 text-sm font-medium text-brand-600 transition-colors hover:bg-brand-50 dark:text-brand-400 dark:hover:bg-brand-950"
                  >
                    View all notifications
                  </button>
                </div>
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={toggleThemeMode}
            className="w-9 h-9 flex items-center justify-center text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-900 rounded-lg transition-all hover:scale-105 active:scale-95"
            title={
              themeMode === 'dark'
                ? 'Switch to light mode'
                : 'Switch to dark mode'
            }
          >
            {themeMode === 'dark' ? (
              <Sun className="w-4 h-4 text-brand-600 dark:text-brand-400" />
            ) : (
              <Moon className="w-4 h-4 text-brand-600 dark:text-brand-400" />
            )}
          </button>

          <div className="relative" ref={userRef}>
            <button
              type="button"
              onClick={() => setShowUserDropdown((prev) => !prev)}
              className="h-9 px-2 flex items-center gap-1.5 text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-900 rounded-lg transition-colors"
            >
              <div className="relative w-8 h-8">
                <UserAvatar
                  src={profileImageUrl}
                  alt={userName || userEmail || 'User'}
                  size="sm"
                  objectPosition="50% 30%"
                  className="text-white font-medium transition-all"
                  style={{ backgroundColor: 'var(--color-brand-600)' }}
                />
                {/* Bottom-left glow indicator */}
                <div
                  className="absolute -bottom-0.5 -left-0.5 w-2 h-2 bg-brand-400 rounded-full border border-white dark:border-neutral-900 animate-pulse"
                  style={{ boxShadow: '0 0 6px var(--color-brand-400)' }}
                />
                {/* Top-right glow indicator */}
                <div
                  className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-brand-500 rounded-full border border-white dark:border-neutral-900"
                  style={{ boxShadow: '0 0 6px var(--color-brand-500)' }}
                />
              </div>
              <ChevronDown className="w-3 h-3 hidden lg:inline" />
            </button>
            {showUserDropdown && (
              <div className="absolute right-0 top-full mt-2 w-72 bg-white dark:bg-neutral-950 border border-neutral-200 dark:border-neutral-800 rounded-lg shadow-xl overflow-hidden">
                <div className="p-4 border-b border-neutral-200 dark:border-neutral-800">
                  <div className="flex items-start gap-3">
                    <div className="relative w-14 h-14 shrink-0">
                      <UserAvatar
                        src={profileImageUrl}
                        alt={userName || userEmail || 'User'}
                        size="lg"
                        objectPosition="50% 30%"
                        className="text-white font-medium transition-all"
                        style={{ backgroundColor: 'var(--color-brand-600)' }}
                      />
                      {/* Bottom-left glow indicator */}
                      <div
                        className="absolute -bottom-1 -left-1 w-3 h-3 bg-brand-400 rounded-full border-2 border-white dark:border-neutral-950 animate-pulse"
                        style={{ boxShadow: '0 0 8px var(--color-brand-400)' }}
                      />
                      {/* Top-right glow indicator */}
                      <div
                        className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-brand-500 rounded-full border-2 border-white dark:border-neutral-950"
                        style={{ boxShadow: '0 0 6px var(--color-brand-500)' }}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-neutral-900 dark:text-white mb-0.5">
                        {userName}
                      </div>
                      <div className="text-xs text-neutral-600 dark:text-neutral-400 mb-1">
                        {userEmail}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-1">
                  <button
                    type="button"
                    onClick={handleEditProfile}
                    className="w-full px-3 py-2 text-left text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-900 rounded transition-colors flex items-center gap-2"
                  >
                    <User className="w-4 h-4" />
                    <span>My Profile</span>
                  </button>
                  <button
                    type="button"
                    onClick={handleChangePassword}
                    className="w-full px-3 py-2 text-left text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-900 rounded transition-colors flex items-center gap-2"
                  >
                    <KeyRound className="w-4 h-4" />
                    <span>Change Password</span>
                  </button>
                </div>

                <div className="p-1 border-t border-neutral-200 dark:border-neutral-800">
                  <button
                    type="button"
                    onClick={() => {
                      setShowUserDropdown(false);
                      onRequestLogout();
                    }}
                    className="w-full px-3 py-2 text-left text-sm text-error-600 dark:text-error-400 hover:bg-error-50 dark:hover:bg-error-950 rounded transition-colors flex items-center gap-2"
                  >
                    <LogOut className="w-4 h-4" />
                    <span>Logout</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <ChangePasswordModal
        isOpen={showChangePasswordModal}
        onClose={() => setShowChangePasswordModal(false)}
      />
    </header>
  );
};

export default Header;
